import { Component, EventEmitter, inject, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FcpModalService, ModalSizes, TooltipPosition, TooltipPositionX, TooltipPositionY } from 'fc-ui-components';
import { ImageResolution, platformAvailableLanguages } from 'helpers';
import { UserService } from 'services';
import { VideoDeliveryFeatureService } from 'app/content/services/video-delivery-feature.service';
import {
    DEFAULT_DOWNLOAD_SELECTION,
    DownloadSelection,
    DownloadSelectionContext,
    DownloadSelectionMetadata
} from './download-selection.types';
import { DownloadSelectionService } from './download-selection.service';

enum CsvLanguageOption {
    UserPreferredLanguage = 'preferredLanguage',
    All = 'all'
}

@Component({
    selector: 'fc-download-selection-modal',
    templateUrl: './download-selection-modal.component.html',
    styleUrls: ['./download-selection-modal.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class DownloadSelectionModalComponent implements OnInit {
    @Input() public id: string;
    @Input() public title: string;
    @Input() public size = ModalSizes.Custom;
    @Input() public defaultSelection: Partial<DownloadSelection> = {};

    @Output() public confirmed = new EventEmitter<DownloadSelection>();
    @Output() public cancelled = new EventEmitter<void>();

    private readonly videoDeliveryFeatureService = inject(VideoDeliveryFeatureService);
    public readonly videoDownloadEnabled = this.videoDeliveryFeatureService.enabled;
    public readonly resolutionOptions = [
        { value: ImageResolution.Original, text: 'DOWNLOAD_CONTENT_MODAL.DOWNLOADS.SIZE.ORIGINAL' },
        { value: ImageResolution.Large, text: 'DOWNLOAD_CONTENT_MODAL.DOWNLOADS.SIZE.LARGE' },
        { value: ImageResolution.Medium, text: 'DOWNLOAD_CONTENT_MODAL.DOWNLOADS.SIZE.MEDIUM' },
        { value: ImageResolution.Thumb, text: 'DOWNLOAD_CONTENT_MODAL.DOWNLOADS.SIZE.SMALL' }
    ];
    public csvLanguageOptions;
    public selection: DownloadSelection = { ...DEFAULT_DOWNLOAD_SELECTION };
    public isTooltipVisible = false;
    public tooltipPosition: TooltipPosition = {
        originX: TooltipPositionX.Center,
        originY: TooltipPositionY.Bottom
    };

    constructor(
        private fcpModalService: FcpModalService,
        private userService: UserService,
        private downloadSelectionService: DownloadSelectionService
    ) {}

    ngOnInit() {
        this.csvLanguageOptions = [
            {
                value: CsvLanguageOption.UserPreferredLanguage,
                text: platformAvailableLanguages[this.userService.getPreferredLanguage()].name,
                hint: 'BULK_GTIN_SEARCH.CSV_LANGUAGE_OPTIONS.PREFERRED_LANGUAGE.HINT'
            },
            {
                value: CsvLanguageOption.All,
                text: 'BULK_GTIN_SEARCH.CSV_LANGUAGE_OPTIONS.ALL_AVAILABLE.TEXT',
                hint: 'BULK_GTIN_SEARCH.CSV_LANGUAGE_OPTIONS.ALL_AVAILABLE.HINT'
            }
        ];
    }

    public open(context?: DownloadSelectionContext, overrides: Partial<DownloadSelection> = {}) {
        if (context) {
            this.downloadSelectionService.setPendingContext(context);
        }
        this.selection = this.downloadSelectionService.getDefaultSelection({
            ...this.defaultSelection,
            ...overrides
        });
        this.fcpModalService.open(this.id);
    }

    public get downloadMetadata(): DownloadSelectionMetadata | null {
        return this.downloadSelectionService.getDownloadMetadata();
    }

    public get showImageCountBadge(): boolean {
        return (this.downloadMetadata?.imageCount ?? 0) > 0;
    }

    public get showVideoCountBadge(): boolean {
        return this.videoDownloadEnabled() && (this.downloadMetadata?.videoCount ?? 0) > 0;
    }

    public onImagesToggleChange(enabled: boolean) {
        this.selection = {
            ...this.selection,
            images: enabled,
            resolution: enabled ? (this.selection.resolution ?? DEFAULT_DOWNLOAD_SELECTION.resolution) : null
        };
    }

    public onVideosToggleChange(enabled: boolean) {
        if (!this.videoDownloadEnabled()) {
            return;
        }
        this.selection = { ...this.selection, video: enabled };
    }

    public onProductAttributesToggleChange(enabled: boolean) {
        this.selection = {
            ...this.selection,
            productAttributes: enabled,
            csvLanguage: enabled ? (this.selection.csvLanguage ?? DEFAULT_DOWNLOAD_SELECTION.csvLanguage) : null
        };
    }

    public showTooltip(visible: boolean): void {
        this.isTooltipVisible = visible;
    }

    public closeModal() {
        this.fcpModalService.close(this.id);
        this.resetAndClearContext();
        this.cancelled.emit();
    }

    public onResolutionSelected(resolution: ImageResolution) {
        this.selection = { ...this.selection, resolution };
    }

    public onCsvLanguageSelected(language: string) {
        this.selection = { ...this.selection, csvLanguage: language };
    }

    public downloadContent() {
        const selection: DownloadSelection = {
            ...this.selection,
            video: this.videoDownloadEnabled() ? this.selection.video : false,
            resolution: this.selection.images ? this.selection.resolution : null,
            csvLanguage: this.selection.productAttributes ? this.selection.csvLanguage : null
        };

        this.confirmed.emit(selection);
        this.fcpModalService.close(this.id);
        this.reset();
    }

    public get isDownloadDisabled(): boolean {
        return (
            !this.downloadSelectionService.isDownloadEnabled(this.selection) ||
            !this.downloadSelectionService.isSubOptionSelectionValid(this.selection)
        );
    }

    private reset() {
        this.selection = this.downloadSelectionService.getDefaultSelection(this.defaultSelection);
    }

    private resetAndClearContext() {
        this.reset();
        this.downloadSelectionService.clearPendingContext();
    }
}
